/**
 * The account-state controls, and why there are three of them over one field.
 *
 * ## They are not one thing
 *
 * A **suspension** is a compliance decision: a person looked at an account and stopped it.
 * A **lockout** is a security measure, usually set by the failed-PIN policy rather than by
 * anybody, and it expires. **Inactive** is a lifecycle state: dormant, not blocked, not
 * closed. They have different causes, different reversals, and different things to say to
 * the customer, so the console renders three labelled controls and each one states what it
 * actually does rather than one dropdown listing five words.
 *
 * ## They are, nevertheless, one column
 *
 * The contract stores all three in `AdminUserStatus`, so only one can be in force at a time
 * and setting any of them replaces whatever was there. That is a real constraint and the page
 * says so out loud (see `SHARED_COLUMN_NOTE`) instead of letting three independent-looking
 * switches imply otherwise. Three honest controls over one field beats one dishonest control,
 * and beats three that pretend to be independent.
 *
 * ## Verification is not on this list at all
 *
 * `verified` is a KYC fact, not an account state, and the contract is right to keep it on
 * `kycStatus`: an account can be verified *and* suspended, and a single column could only ever
 * hold one of those. So verification gets its own control that leads to the review queue,
 * which is the only thing that can actually decide it. `verificationOf` below is what the page
 * renders for it.
 */
import type {
  AccountState,
  AdminUserDetail,
  KycStatus,
  OperatorSettableState,
} from '../contracts/types.js';

/** What each state means for the person holding the account, in words an operator can read out. */
export const ACCOUNT_STATE_COPY: Readonly<Record<AccountState, { label: string; effect: string }>> = {
  active: {
    label: 'Active',
    effect: 'Nothing is blocked. The customer can sign in and move money normally.',
  },
  suspended: {
    label: 'Suspended',
    effect:
      'Every request is refused and every session is signed out. The customer is told the account is unavailable, never why.',
  },
  inactive: {
    label: 'Inactive',
    effect:
      'Sign-in still works, but no money can move until the account is restored. Dormant rather than blocked.',
  },
  locked: {
    label: 'Locked',
    effect:
      'A timed lockout. The customer cannot get in until it expires or an operator clears it.',
  },
  closed: {
    label: 'Closed',
    effect: 'The customer closed this account themselves. Nothing here can reopen it.',
  },
};

/**
 * The one sentence that keeps three controls from lying about a single column.
 *
 * Printed once, above the controls, rather than repeated on each: an operator reads it before
 * they touch anything, and three copies of the same caveat is noise that gets skipped.
 */
export const SHARED_COLUMN_NOTE =
  'These three share one account state, so setting any of them replaces whatever is in force now. Verification is separate and is not affected by any of them.';

export interface AccountStateControl {
  readonly id: 'suspension' | 'lockout' | 'lifecycle';
  readonly label: string;
  /** True when this restriction is the state currently in force. */
  readonly on: boolean;
  /** What being in this state does to the customer. */
  readonly effect: string;
  /** When it started, where the API records it. */
  readonly since: string | null;
  readonly reason: string | null;
  /** The button: what it says, where it lands, and whether it takes something away. */
  readonly action: {
    readonly label: string;
    readonly state: OperatorSettableState;
    /** Restrictive directions go through a typed-reason confirmation; releases do not. */
    readonly restrictive: boolean;
    /** What the confirm dialog warns about, in one sentence. */
    readonly consequence: string;
  };
  /** Non-null when the control cannot be used at all, saying why. */
  readonly blocked: string | null;
}

/**
 * The three controls, in the order an operator reaches for them.
 *
 * A closed account blocks all three, because closure is the customer's own act and terminal:
 * a console that offered to reactivate it would be offering something the API refuses, and a
 * button that always errors is worse than no button.
 */
export function accountControlsOf(user: AdminUserDetail): readonly AccountStateControl[] {
  const closed = user.status === 'closed';
  const blocked = closed
    ? 'This account was closed by the customer. Account states cannot be changed on a closed account.'
    : null;

  return [
    {
      id: 'suspension',
      label: 'Suspension',
      on: user.status === 'suspended',
      effect: ACCOUNT_STATE_COPY.suspended.effect,
      since: null,
      reason: user.suspendedReason,
      action:
        user.status === 'suspended'
          ? {
              label: 'Lift the suspension',
              state: 'active',
              restrictive: false,
              consequence: 'The customer can sign in and move money again immediately.',
            }
          : {
              label: 'Suspend account',
              state: 'suspended',
              restrictive: true,
              consequence:
                'Every session is signed out and every request is refused until an operator lifts this.',
            },
      blocked,
    },
    {
      id: 'lockout',
      label: 'Security lockout',
      on: user.status === 'locked',
      effect: ACCOUNT_STATE_COPY.locked.effect,
      since: user.lockedUntil,
      reason: null,
      action:
        user.status === 'locked'
          ? {
              label: 'Clear the lockout',
              state: 'active',
              restrictive: false,
              consequence: 'The customer can sign in again straight away.',
            }
          : {
              label: 'Lock account',
              state: 'locked',
              restrictive: true,
              consequence:
                'The customer cannot sign in until the lockout expires or an operator clears it.',
            },
      blocked,
    },
    {
      id: 'lifecycle',
      label: 'Lifecycle',
      on: user.status === 'inactive',
      effect: ACCOUNT_STATE_COPY.inactive.effect,
      since: user.inactiveAt,
      reason: null,
      action:
        user.status === 'inactive'
          ? {
              label: 'Make active',
              state: 'active',
              restrictive: false,
              consequence: 'The account returns to normal service and money can move again.',
            }
          : {
              label: 'Make inactive',
              state: 'inactive',
              restrictive: true,
              consequence:
                'The customer can still sign in, but no money moves until the account is made active again.',
            },
      blocked,
    },
  ];
}

export interface VerificationView {
  readonly verified: boolean;
  readonly status: KycStatus;
  /** What this KYC status means for the customer's limits, in one line. */
  readonly effect: string;
  /** How many identity documents are on file. */
  readonly documentCount: number;
  /**
   * Why this is not a toggle. Rendered beside the control, because an operator who expected a
   * switch is owed the reason there is not one rather than left to conclude it is missing.
   */
  readonly note: string;
}

const KYC_EFFECT: Readonly<Record<KycStatus, string>> = {
  none: 'No identity check has been submitted. The account sits at its opening tier.',
  pending: 'A submission is waiting on a reviewer. Limits stay at the current tier until it is decided.',
  verified: 'Identity confirmed. The account holds the tier the submission was approved for.',
  rejected: 'The last submission was refused. The customer can submit again.',
};

/**
 * Verification, as its own thing.
 *
 * Deliberately read-only here. Verification is decided by approving a submission in the review
 * queue, which is where the document, the vendor's answer and the reviewer's reason all live;
 * a second path that flipped the flag from this page would produce a verified account with no
 * submission behind it and nothing in the audit trail explaining why.
 */
export function verificationOf(user: AdminUserDetail): VerificationView {
  return {
    verified: user.kycStatus === 'verified',
    status: user.kycStatus,
    effect: KYC_EFFECT[user.kycStatus],
    documentCount: user.kycDocuments.length,
    // "the ID checks page", which is what the sidebar entry and the page itself have been
    // called since the plain-language pass. This line still said "the KYC review", so it sent
    // an operator looking for a page that no longer goes by that name anywhere in the console.
    note: 'Verification is decided on the ID checks page, not here. That is where the document, the vendor result and the reviewer reason are recorded together.',
  };
}

/**
 * Whether a state change needs a reason.
 *
 * Mirrors `setAccountStateRequestSchema`'s own rule: anything restrictive does, releasing to
 * `active` does not. Mirrored rather than assumed so the console asks for a reason exactly
 * when the API will refuse without one, and never asks for one it is going to discard.
 */
export function reasonRequiredFor(state: OperatorSettableState): boolean {
  return state !== 'active';
}

/** The lockout durations offered, and what each is for. Minutes, matching `lockMinutes`. */
export const LOCK_DURATIONS: readonly { readonly minutes: number; readonly label: string }[] = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 60 * 12, label: '12 hours' },
  { minutes: 60 * 24, label: '1 day' },
  { minutes: 60 * 24 * 7, label: '7 days' },
  // The contract's ceiling is 43,200 minutes, which is thirty days. Offering the maximum as a
  // named option matters: a stolen-handset lockout is the case an operator sets by hand, and
  // making them work out how many minutes are in a month is how it gets set to 30 by mistake.
  { minutes: 43_200, label: '30 days' },
];
